import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronDown, X, SlidersHorizontal, ArrowUpDown } from "lucide-react";
import { fetchProducts } from "../lib/api";
import ProductCard from "../components/shop/ProductCard";
import ComingSoonPage from "../components/shared/ComingSoonPage";
import { CATEGORIES, DIWALI_INFO } from "../constants/data";

const SORT_OPTIONS = [
  { value: "latest",       label: "Latest Collection" },
  { value: "price_asc",    label: "Price: Low to High" },
  { value: "price_desc",   label: "Price: High to Low" },
  { value: "best_selling", label: "Best Selling" },
];

const PRICE_RANGES = [
  { key: "all",   label: "All Prices",       min: null, max: null },
  { key: "1-2k",  label: "₹1,000 – ₹2,000",  min: 1000, max: 2000 },
  { key: "2-3k",  label: "₹2,000 – ₹3,000",  min: 2000, max: 3000 },
  { key: "3k+",   label: "₹3,000 & above",   min: 3000, max: null },
];

export default function Shop() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("latest");
  const [priceKey, setPriceKey] = useState("all");
  const [sortOpen, setSortOpen] = useState(false);      // desktop dropdown
  const [sheet, setSheet] = useState(null);             // mobile bottom-sheet: "sort" | "price" | null
  const cat = CATEGORIES.find((c) => c.slug === category) || CATEGORIES[0];

  const priceRange = useMemo(
    () => PRICE_RANGES.find((r) => r.key === priceKey) || PRICE_RANGES[0],
    [priceKey],
  );

  useEffect(() => {
    setLoading(true);
    const params = { category: cat.slug, sort };
    if (priceRange.min !== null) params.price_min = priceRange.min;
    if (priceRange.max !== null) params.price_max = priceRange.max;
    fetchProducts(params).then((data) => { setProducts(data); setLoading(false); });
  }, [cat.slug, sort, priceRange.min, priceRange.max]);

  // Expand each product into 1 base card + N variant cards.
  // Variants also honour the current price-range filter (they may be priced differently).
  const displayItems = useMemo(() => {
    const min = priceRange.min ?? -Infinity;
    const max = priceRange.max ?? Infinity;
    const items = [];
    for (const p of products) {
      // Base card
      items.push({ key: p.id, product: p });
      // One card per variant with its own images/price
      for (const v of (p.variants || [])) {
        const price = v.price ?? p.price;
        if (price < min || price > max) continue;
        items.push({ key: `${p.id}::${v.id}`, product: p, variant: v });
      }
    }
    // If sort is by price, re-sort combined list so variants slot into their correct place
    if (sort === "price_asc") {
      items.sort((a, b) => ((a.variant?.price ?? a.product.price) - (b.variant?.price ?? b.product.price)));
    } else if (sort === "price_desc") {
      items.sort((a, b) => ((b.variant?.price ?? b.product.price) - (a.variant?.price ?? a.product.price)));
    }
    return items;
  }, [products, priceRange.min, priceRange.max, sort]);

  // Lock body scroll while sheet is open
  useEffect(() => {
    document.body.style.overflow = sheet ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sheet]);

  const currentSortLabel = SORT_OPTIONS.find((o) => o.value === sort)?.label || "Latest Collection";
  const sortActive = sort !== "latest";
  const priceActive = priceKey !== "all";

  if (cat.slug === "diwali") {
    return (
      <ComingSoonPage
        pageId="diwali"
        pageTestId="shop-diwali"
        titleLine1="Diwali Hampers"
        titleLine2={DIWALI_INFO.greeting}
        description={<>Our festive <b>Diwali collection</b> — sweets, diyas, dry fruits &amp; handcrafted keepsakes — is being wrapped with love. Preview the hampers on our homepage!</>}
        subCopy="Drop your email and we'll let you know the moment the collection goes live."
        accentColor="#E08036"
      />
    );
  }

  return (
    <div data-testid={`shop-${cat.slug}`}>
      <section className="pt-32 pb-8 sm:pb-12 bg-[#F2E8E4]/40 border-b border-[#E5DBD8]">
        <div className="container-x">
          <p className="label-small">Collection</p>
          <h1 className="font-serif text-4xl sm:text-6xl mt-2 sm:mt-3 text-[#2D2424]">{cat.name}</h1>
          <p className="text-sm sm:text-base text-[#5C5555] mt-2 sm:mt-4 max-w-2xl">{cat.description}</p>
        </div>
      </section>

      <section className="section-y">
        <div className="container-x">
          {/* Category pills — horizontal scroll on mobile, wrap on desktop */}
          <div
            className="flex sm:flex-wrap gap-2 sm:gap-3 mb-5 sm:mb-8 -mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto sm:overflow-visible scrollbar-thin"
            data-testid="shop-categories"
          >
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/shop/${c.slug}`}
                data-testid={`shop-filter-${c.slug}`}
                className={`shrink-0 px-4 sm:px-5 py-2 text-[10px] sm:text-xs tracking-[0.2em] uppercase border whitespace-nowrap ${
                  c.slug === cat.slug ? "bg-[#7A1F2B] text-[#F2E8E4] border-[#7A1F2B]" : "border-[#E5DBD8] text-[#2D2424] hover:border-[#7A1F2B]"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>

          {/* Toolbar */}
          <div className="mb-6 pb-4 border-b border-[#E5DBD8]" data-testid="shop-toolbar">
            {/* --- Mobile: 2 compact buttons side-by-side --- */}
            <div className="sm:hidden flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSheet("sort")}
                data-testid="mobile-sort-btn"
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-xs tracking-wider uppercase rounded-full border ${
                  sortActive ? "bg-[#7A1F2B] text-white border-[#7A1F2B]" : "bg-white text-[#2D2424] border-[#E5DBD8]"
                }`}
              >
                <ArrowUpDown className="w-3.5 h-3.5"/> Sort
              </button>
              <button
                type="button"
                onClick={() => setSheet("price")}
                data-testid="mobile-price-btn"
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-xs tracking-wider uppercase rounded-full border ${
                  priceActive ? "bg-[#7A1F2B] text-white border-[#7A1F2B]" : "bg-white text-[#2D2424] border-[#E5DBD8]"
                }`}
              >
                <SlidersHorizontal className="w-3.5 h-3.5"/> Price {priceActive && <span className="text-[10px]">•</span>}
              </button>
            </div>

            {/* --- Desktop: chips + dropdown --- */}
            <div className="hidden sm:flex sm:items-center sm:justify-between gap-4">
              {/* Price range chips */}
              <div className="flex flex-wrap items-center gap-2" data-testid="price-range-chips">
                <span className="text-[10px] tracking-widest uppercase text-[#5C5555] mr-1">Price</span>
                {PRICE_RANGES.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setPriceKey(r.key)}
                    data-testid={`price-range-${r.key}`}
                    className={`px-3 py-1.5 text-xs tracking-wider rounded-full border transition-colors ${
                      priceKey === r.key
                        ? "bg-[#7A1F2B] text-white border-[#7A1F2B]"
                        : "bg-white text-[#2D2424] border-[#E5DBD8] hover:border-[#7A1F2B]"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>

              {/* Sort dropdown */}
              <div className="relative shrink-0" data-testid="sort-container">
                <button
                  type="button"
                  onClick={() => setSortOpen((o) => !o)}
                  data-testid="sort-toggle"
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-wider uppercase border border-[#E5DBD8] bg-white hover:border-[#7A1F2B] rounded-full min-w-[220px] justify-between"
                >
                  <span className="text-[10px] tracking-widest uppercase text-[#5C5555]">Sort</span>
                  <span className="text-[#2D2424] flex-1 text-left">{currentSortLabel}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${sortOpen ? "rotate-180" : ""}`} />
                </button>
                {sortOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setSortOpen(false)} aria-hidden="true" />
                    <div className="absolute right-0 mt-1 w-full bg-white border border-[#E5DBD8] rounded-lg shadow-lg z-20 overflow-hidden" data-testid="sort-menu">
                      {SORT_OPTIONS.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          onClick={() => { setSort(o.value); setSortOpen(false); }}
                          data-testid={`sort-option-${o.value}`}
                          className={`w-full px-4 py-2.5 text-xs tracking-wider text-left hover:bg-[#F2E8E4] transition-colors ${
                            sort === o.value ? "bg-[#FFE7F1] text-[#7A1F2B] font-semibold" : "text-[#2D2424]"
                          }`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <p className="text-center text-[#5C5555] py-20">Loading…</p>
          ) : displayItems.length === 0 ? (
            <div className="text-center py-20" data-testid="shop-empty">
              <p className="text-[#5C5555]">No products match this filter.</p>
              {priceKey !== "all" && (
                <button
                  onClick={() => setPriceKey("all")}
                  data-testid="clear-price-filter"
                  className="mt-3 text-xs uppercase tracking-wider text-[#7A1F2B] underline"
                >
                  Clear price filter
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="mb-4 text-xs text-[#5C5555]" data-testid="shop-count">
                Showing <span className="text-[#2D2424] font-semibold">{displayItems.length}</span> product{displayItems.length !== 1 ? "s" : ""}
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                {displayItems.map(({ key, product, variant }) => (
                  <ProductCard key={key} product={product} variant={variant}/>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Mobile bottom-sheet for Sort / Price */}
      {sheet && (
        <div
          className="fixed inset-0 z-[80] sm:hidden bg-[#2D2424]/40 backdrop-blur-sm flex items-end"
          onClick={(e) => { if (e.target === e.currentTarget) setSheet(null); }}
          data-testid={`mobile-sheet-${sheet}`}
        >
          <div className="w-full bg-white rounded-t-2xl pb-safe animate-in slide-in-from-bottom duration-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5DBD8]">
              <p className="font-serif text-lg text-[#2D2424]">{sheet === "sort" ? "Sort by" : "Price"}</p>
              <button
                type="button"
                onClick={() => setSheet(null)}
                data-testid="mobile-sheet-close"
                className="w-8 h-8 rounded-full border border-[#E5DBD8] flex items-center justify-center hover:bg-[#F2E8E4]"
                aria-label="close"
              >
                <X className="w-4 h-4"/>
              </button>
            </div>
            <div className="py-2 max-h-[65vh] overflow-y-auto">
              {sheet === "sort" && SORT_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { setSort(o.value); setSheet(null); }}
                  data-testid={`mobile-sort-option-${o.value}`}
                  className={`w-full px-4 py-3 text-sm text-left flex items-center justify-between hover:bg-[#F2E8E4] transition-colors ${
                    sort === o.value ? "bg-[#FFE7F1] text-[#7A1F2B] font-semibold" : "text-[#2D2424]"
                  }`}
                >
                  <span>{o.label}</span>
                  {sort === o.value && <span className="text-[#7A1F2B] text-lg leading-none">✓</span>}
                </button>
              ))}
              {sheet === "price" && PRICE_RANGES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => { setPriceKey(r.key); setSheet(null); }}
                  data-testid={`mobile-price-option-${r.key}`}
                  className={`w-full px-4 py-3 text-sm text-left flex items-center justify-between hover:bg-[#F2E8E4] transition-colors ${
                    priceKey === r.key ? "bg-[#FFE7F1] text-[#7A1F2B] font-semibold" : "text-[#2D2424]"
                  }`}
                >
                  <span>{r.label}</span>
                  {priceKey === r.key && <span className="text-[#7A1F2B] text-lg leading-none">✓</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
